import { Task } from '@otw/models/tasks';
import { db } from '@otw/models/db';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'contexts/AuthContext';
import { useSnackbar } from '@/hooks/common/useSnackbar';
import { addDoc, deleteDoc, doc, getDocs, updateDoc } from 'firebase/firestore';

type UseTasksOptions = {
  /** When false, skip the full tasks list query (mutations only). Default true. */
  fetchList?: boolean;
};

interface UseTasksReturn {
  tasks: Task[] | undefined;
  isLoading: boolean;
  error: Error | null;
  createTask: (
    task: Omit<Task, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'completedAt' | 'status'> &
      Partial<Pick<Task, 'status'>>,
  ) => Promise<Task>;
  updateTask: (args: { id: string; updates: Partial<Task> }) => Promise<{ id: string } & Partial<Task>>;
  deleteTask: (id: string) => Promise<string>;
  toggleTaskComplete: (task: Task) => Promise<void>;
  getTasksByContactId: (contactId: string) => Task[] | undefined;
  getTasksForUser: (userId: string) => Task[] | undefined;
}

export const useTasks = (options?: UseTasksOptions): UseTasksReturn => {
  const fetchList = options?.fetchList !== false;
  const { showSuccess, showError } = useSnackbar();
  const queryClient = useQueryClient();
  const { userInfo } = useAuth();
  const queryKey = ['tasks'];

  const {
    data: tasks,
    isLoading,
    error,
  } = useQuery<Task[]>({
    queryKey,
    queryFn: async () => {
      const snapshot = await getDocs(db.tasks);
      return snapshot.docs.map((d) => d.data());
    },
    enabled: fetchList,
    staleTime: 1000 * 60 * 5,
  });

  const createTaskMutation = useMutation({
    mutationFn: async (
      input: Omit<Task, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'completedAt' | 'status'> &
        Partial<Pick<Task, 'status'>>,
    ) => {
      const now = new Date();
      const newTask = {
        ...input,
        status: input.status || 'pending',
        completedAt: null,
        createdAt: now,
        updatedAt: now,
        createdBy: userInfo?.id || 'unknown',
      } as Task;

      const docRef = await addDoc(db.tasks, newTask);
      return { ...newTask, id: docRef.id } as Task;
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      showSuccess('Task created');
    },
    onError: () => {
      showError('Could not create task');
    },
  });

  const updateTaskMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Task> }) => {
      const taskRef = doc(db.tasks, id);
      const now = new Date();
      const payload: Partial<Task> = { ...updates, updatedAt: now };
      await updateDoc(taskRef, payload as any);
      return { id, ...payload };
    },
    onMutate: async ({ id, updates }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<Task[]>(queryKey);
      queryClient.setQueryData<Task[]>(queryKey, (old) =>
        (old || []).map((task) => (task.id === id ? { ...task, ...updates, updatedAt: new Date() } : task)),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      queryClient.setQueryData(queryKey, context?.previous);
      showError('Could not update task');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      showSuccess('Task updated');
    },
  });

  const deleteTaskMutation = useMutation({
    mutationFn: async (id: string) => {
      await deleteDoc(doc(db.tasks, id));
      return id;
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<Task[]>(queryKey);
      queryClient.setQueryData<Task[]>(queryKey, (old) => (old || []).filter((t) => t.id !== id));
      return { previous };
    },
    onError: (_err, _vars, context) => {
      queryClient.setQueryData(queryKey, context?.previous);
      showError('Could not delete task');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      showSuccess('Task deleted');
    },
  });

  const toggleTaskComplete = async (task: Task) => {
    const isComplete = task.status === 'completed';
    await updateTaskMutation.mutateAsync({
      id: task.id,
      updates: {
        status: isComplete ? 'pending' : 'completed',
        completedAt: isComplete ? null : new Date(),
      },
    });
  };

  const getTasksByContactId = (contactId: string) =>
    tasks?.filter((task) => task.contact_id === contactId);

  const getTasksForUser = (userId: string) =>
    tasks?.filter((task) => String(task.assignedToId) === String(userId));

  return {
    tasks,
    isLoading,
    error: error as Error | null,
    createTask: createTaskMutation.mutateAsync,
    updateTask: updateTaskMutation.mutateAsync,
    deleteTask: deleteTaskMutation.mutateAsync,
    toggleTaskComplete,
    getTasksByContactId,
    getTasksForUser,
  };
};
